export const SORT_DIRECTIONS = ['asc', 'desc'] as const;

export type SortDirection = (typeof SORT_DIRECTIONS)[number];
export type TableQueryValue = string | number | boolean | undefined | null;
export type TableQuery = Record<string, TableQueryValue>;

export type SortState<TSortField extends string> = {
  sortBy: TSortField;
  sortDirection: SortDirection;
};

export function resolveSortState<TSortField extends string>({
  allowedSortFields,
  defaultSort,
  searchParams,
}: {
  allowedSortFields: readonly TSortField[];
  defaultSort: SortState<TSortField>;
  searchParams?: {
    sortBy?: string;
    sortDirection?: string;
  };
}): SortState<TSortField> {
  const sortBy = allowedSortFields.includes(searchParams?.sortBy as TSortField)
    ? (searchParams?.sortBy as TSortField)
    : defaultSort.sortBy;

  const sortDirection = SORT_DIRECTIONS.includes(searchParams?.sortDirection as SortDirection)
    ? (searchParams?.sortDirection as SortDirection)
    : defaultSort.sortDirection;

  return { sortBy, sortDirection };
}

export function getNextSortDirection<TSortField extends string>({
  currentSort,
  nextSortBy,
  initialDirection = 'asc',
}: {
  currentSort: SortState<TSortField>;
  nextSortBy: TSortField;
  initialDirection?: SortDirection;
}): SortDirection {
  if (currentSort.sortBy !== nextSortBy) {
    return initialDirection;
  }

  return currentSort.sortDirection === 'asc' ? 'desc' : 'asc';
}

export function buildTableHref({
  basePath,
  query,
  overrides,
}: {
  basePath: string;
  query?: TableQuery;
  overrides?: TableQuery;
}) {
  const [path, baseQueryString] = basePath.split('?');
  const searchParams = new URLSearchParams(baseQueryString ?? '');

  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === undefined || value === null) {
      searchParams.delete(key);
      continue;
    }

    searchParams.set(key, typeof value === 'boolean' ? (value ? 'true' : 'false') : String(value));
  }

  for (const [key, value] of Object.entries(overrides ?? {})) {
    if (value === undefined || value === null) {
      searchParams.delete(key);
      continue;
    }

    searchParams.set(key, typeof value === 'boolean' ? (value ? 'true' : 'false') : String(value));
  }

  const searchString = searchParams.toString();

  return searchString ? `${path}?${searchString}` : path;
}
